import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import oktaAuth from '../modules/auth/OktaAuth';
import {
    Customer,
    CustomerCreationInput,
    ExportStatusResponse,
    HeaderResponse,
    HeaderVerboseResponse,
    StageEnum,
    StageResponse,
    SubmitHeaderInput,
    SubmitHeaderResponse,
    SubmitStageInput,
} from './MaturityModelApiTypes';

// Define a service using a base URL and expected endpoints
export const maturityModelApi = createApi({
    reducerPath: 'maturityModelApi',
    tagTypes: ['Customers'],
    baseQuery: fetchBaseQuery({
        baseUrl: process.env.REACT_APP_BASE_URL || 'http://localhost:5000/api/',
        prepareHeaders: (headers) => {
            // , { getState }
            // const token = (getState() as RootState).auth.token;
            const idToken = oktaAuth.getIdToken();
            // If we have a token set in state, let's assume that we should be passing it.
            if (idToken) {
                headers.set('authorization', `Bearer ${idToken}`);
            }

            return headers;
        },
    }),
    endpoints: (builder) => ({
        getCustomers: builder.query<Customer[], null>({
            query: () => `customers`,
            providesTags: ['Customers'],
        }),
        submitHeader: builder.mutation<SubmitHeaderResponse, SubmitHeaderInput>({
            query: (input) => ({
                url: `maturity/header`,
                method: 'POST',
                body: { ...input },
            }),
        }),
        getQuestions: builder.query<StageResponse, StageEnum>({
            query: (stage) => `stage/questions?stage_name=${stage}`,
        }),
        submitStage: builder.mutation<string, SubmitStageInput>({
            query: (input) => ({
                url: `maturity/lines`,
                method: 'POST',
                body: input,
                responseHandler: 'text',
            }),
        }),
        getHeader: builder.query<HeaderResponse, number>({
            query: (id) => `maturity/header/details?maturity_header_id=${id}`,
            transformResponse: (response: Record<string, HeaderResponse>) => Object.values(response)[0],
        }),
        getHeaders: builder.query<HeaderResponse[], null>({
            query: () => `maturity/header/details`,
        }),
        getHeaderVerbose: builder.query<HeaderVerboseResponse, number>({
            query: (id) => `maturity/header/verbose?maturity_header_id=${id}`,
        }),
        submitExportRequest: builder.mutation<{ export_request_id: number }, { maturity_header_id: number; eplususer_email_id: string }>({
            query: (input) => ({
                url: `export/request`,
                method: 'POST',
                body: { ...input },
            }),
        }),
        getExportStatus: builder.query<ExportStatusResponse, { email: string; headerId: number }>({
            query: ({ email, headerId }) => `export/request?maturity_header_id=${headerId}&eplususer_email_id=${email}`,
        }),
        createCustomer: builder.mutation<{ customer_id: number }, CustomerCreationInput>({
            query: (input) => ({
                url: `customers`,
                method: 'POST',
                body: { ...input },
            }),
            invalidatesTags: ['Customers'],
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetCustomersQuery,
    useSubmitHeaderMutation,
    useGetQuestionsQuery,
    useSubmitStageMutation,
    useGetHeaderQuery,
    useGetHeadersQuery,
    useGetHeaderVerboseQuery,
    useSubmitExportRequestMutation,
    useGetExportStatusQuery,
    useCreateCustomerMutation,
} = maturityModelApi;
